import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { supabase } from "./services/supabase";
import * as styles from "./Home.module.css";
import { FiChevronRight, FiBarChart2, FiShoppingBag, FiGrid } from "react-icons/fi";
import Logo from "./components/Logo";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [error, setError] = useState(null);

  // Check if user already has a Stripe account
  useEffect(() => {
    async function checkStripeAccount() {
      if (user) {
        try {
          const { data } = await supabase
            .from('user_profiles')
            .select('stripe_connect_id, stripe_account_status')
            .eq('user_id', user.id)
            .single();
          
          if (data && data.stripe_connect_id) {
            setStripeAccountId(data.stripe_connect_id);
            setAccountStatus(data.stripe_account_status);
            
            // Verify status with Stripe API if we have an account ID
            if (data.stripe_connect_id) {
              try {
                const response = await fetch('/check-account-type', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    account_id: data.stripe_connect_id
                  }),
                });
                
                if (response.ok) {
                  const accountData = await response.json();
                  setAccountStatus(
                    accountData.charges_enabled && accountData.payouts_enabled 
                      ? 'active' 
                      : 'pending'
                  );
                }
              } catch (err) {
                console.error("Error checking account with Stripe:", err);
              }
            }
          }
          setInitialCheckDone(true);
        } catch (error) {
          console.error("Error checking Stripe account:", error);
          setInitialCheckDone(true);
        }
      } else {
        setInitialCheckDone(true);
      }
    }
    
    checkStripeAccount();
  }, [user]);

  // Handle setting up Stripe Connect
  const handleStripeConnect = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // First, check if user already has a Stripe account
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('stripe_connect_id')
        .eq('user_id', user.id)
        .single();
      
      if (profile?.stripe_connect_id) {
        // Use the simplified account link creation for existing accounts
        try {
          const response = await fetch('/account-link', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ accountId: profile.stripe_connect_id }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to create account link: ${response.status} - ${errorText}`);
          }

          const data = await response.json();
          
          if (data.url) {
            // Redirect to Stripe's onboarding page
            window.location.href = data.url;
            return;
          } else {
            throw new Error('No URL returned from server');
          }
        } catch (error) {
          console.error('Account link error:', error);
          setError(`Error connecting to Stripe: ${error.message}`);
          setLoading(false);
          return;
        }
      }
      
      // For new accounts, continue with account creation process
      // ...existing code...
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Stripe dashboard access
  const handleStripeDashboard = async () => {
    if (!stripeAccountId) return;
    
    setLoading(true);
    try {
      const response = await fetch('/create-login-link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_id: stripeAccountId }),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not create login link');
      
      window.location.href = data.url;
    } catch (err) {
      console.error("Error creating dashboard link:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  if (!initialCheckDone) {
    return <div className={styles.loadingContainer}>Loading...</div>;
  }

  // If user is logged in, show their dashboard or Stripe setup
  if (user) {
    if (accountStatus === 'active') {
      return (
        <div className={styles.container}>
          <div className={styles.banner}>
            <h1>QR Butler</h1>
            <p>Smart QR Solutions for Restaurants & Retail</p>
          </div>
          
          <div className={styles.content}>
            <div className={styles.dashboardActions}>
              <h2>Welcome back!</h2>
              <p>Your account is ready to take payments.</p>
              
              <div className={styles.actionButtons}>
                <button 
                  onClick={() => navigate('/dashboard')}
                  className={styles.primaryButton}
                >
                  View Dashboard <FiChevronRight />
                </button>
                
                <button 
                  onClick={handleStripeDashboard}
                  disabled={loading}
                  className={styles.secondaryButton}
                >
                  Stripe Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className={styles.container}>
          <div className={styles.banner}>
            <h1>QR Butler</h1>
            <p>Smart QR Solutions for Restaurants & Retail</p>
          </div>
          
          <div className={styles.content}>
            <div className={styles.setupContainer}>
              <h2>Complete Your Setup</h2>
              <p>Connect with Stripe to start receiving payments for orders</p>
              
              {error && <div className={styles.errorMessage}>{error}</div>}
              
              <button 
                onClick={handleStripeConnect}
                disabled={loading}
                className={styles.primaryButton}
                style={{ 
                  cursor: loading ? 'wait' : 'pointer',
                  opacity: loading ? 0.7 : 1
                }}
              >
                {loading ? "Setting up..." : "Set up Stripe Connect"}
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  const PricingSection = () => {
    return (
      <section className={styles.pricing} id="pricing">
        <div className="container">
          <h2 className={styles.sectionTitle}>Simple, Transparent Pricing</h2>
          
          <div className={styles.pricingIntro}>
            <p>No monthly fees or hidden charges. Only pay when your customers order.</p>
          </div>
          
          <div className={styles.pricingCard}>
            <h3>Pay As You Go</h3>
            <div className={styles.price}>
              6% <span style={{ fontSize: '16px', margin: '0 5px' }}></span> 
            </div>
            <p className={styles.priceDetail}>per transaction</p>
            
            <ul className={styles.pricingFeatures}>
              <li>No monthly subscription fees</li>
              <li>Full menu management</li>
              <li>Unlimited QR codes</li>
              <li>Real-time order dashboard</li>
              <li>Basic analytics</li>
              <li>Customer-friendly ordering experience</li>
              <li>Stripe Processing Fees Not Included In Price</li>
            </ul>
            
            <Link to="/signup" className={styles.primaryButton}>
              Start For Free
            </Link>
          </div>
        </div>
      </section>
    );
  };

  // New marketing-focused landing page for non-authenticated users
  return (
    <div className={styles.marketingContainer}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.logo}>
          <Logo color="white" size="large" />
        </div>
        <h1>QR Ordering & Payments<br />With No Monthly Fees</h1>
        <p>Turn any restaurant or hotel into a digital ordering powerhouse without expensive hardware or training.</p>
        <div className={styles.ctaButtons}>
          <Link to="/signup" className={styles.primaryButton}>Get Started <FiChevronRight /></Link>
          <a href="#howItWorks" className={styles.secondaryButton}>How It Works</a>
        </div>
      </section>
      
      {/* Features Section */}
      <section className={styles.features} id="features">
        <h2 className={styles.sectionTitle}>Why Choose QR Butler?</h2>
        <div className={styles.featureGrid}>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-mobile-alt"></i></div>
            <h3>No Special Hardware Needed</h3>
            <p>Use your existing devices - no expensive POS terminals or proprietary hardware required. Just print QR codes and go!</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-dollar-sign"></i></div>
            <h3>No Monthly Fees</h3>
            <p>Only pay when you make money - a simple 6% fee per transaction (not including payment processing). No subscriptions or hidden costs.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-chart-line"></i></div>
            <h3>Detailed Analytics</h3>
            <p>Track sales trends, popular menu items, and customer behavior to optimize your business decisions in real-time.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-users"></i></div>
            <h3>Reduce Labor Costs</h3>
            <p>Let customers browse, order and pay themselves to reduce staffing needs and free up your team to focus on hospitality.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-laptop"></i></div>
            <h3>No App Downloads</h3>
            <p>Works directly in your customers' browsers - no friction, no app downloads, just scan and order.</p>
          </div>
          <div className={styles.featureCard}>
            <div className={styles.featureIcon}><i className="fas fa-clock"></i></div>
            <h3>Set Up in Minutes</h3>
            <p>Create your menu, generate QR codes, and start taking orders the same day. No complex configuration or IT knowledge required.</p>
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className={styles.howItWorks} id="howItWorks">
        <h2 className={styles.sectionTitle}>How QR Butler Works</h2>
        <div className={styles.steps}>
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepContent}>
              <h3>Create Your Digital Menu</h3>
              <p>Sign up and build your digital menu in our simple dashboard. Add items with descriptions, prices, and photos in just minutes.</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepContent}>
              <h3>Generate & Print QR Codes</h3>
              <p>Create custom QR codes for each table with our one-click generator. Print them out and place them at your tables.</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepContent}>
              <h3>Customers Order & Pay</h3>
              <p>Guests scan the code with their phone, browse your menu, and place orders with secure payment - all without downloading an app.</p>
            </div>
          </div>
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepContent}>
              <h3>Fulfill Orders</h3>
              <p>Receive orders instantly on your dashboard and track them through preparation, ready, and completed states.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className={styles.testimonials} id="testimonials">
        <h2 className={styles.sectionTitle}>What Our Customers Say</h2>
        <div className={styles.testimonialGrid}>
          <div className={styles.testimonialCard}>
            <p className={styles.testimonialText}>"We've reduced front-of-house staff by 30% while increasing our average ticket size. QR Butler paid for itself in the first week and setup took less than an hour!"</p>
            <p className={styles.testimonialAuthor}>Michael Rodriguez</p>
            <p className={styles.testimonialRole}>Owner, Coastal Kitchen</p>
          </div>
          <div className={styles.testimonialCard}>
            <p className={styles.testimonialText}>"I was skeptical about digital ordering, but QR Butler has been game-changing for our small restaurant. No monthly fees meant there was zero risk to try it, and now we're seeing 20% higher order values."</p>
            <p className={styles.testimonialAuthor}>Sarah Johnson</p>
            <p className={styles.testimonialRole}>Manager, Urban Plate</p>
          </div>
          <div className={styles.testimonialCard}>
            <p className={styles.testimonialText}>"As a boutique hotel, implementing QR Butler for room service has dramatically improved our guest experience while saving us on staffing costs. The analytics help us understand what our guests really want."</p>
            <p className={styles.testimonialAuthor}>David Chen</p>
            <p className={styles.testimonialRole}>General Manager, Harbor View Hotel</p>
          </div>
        </div>
      </section>
      
      {/* Pricing Section */}
      <PricingSection />
      
      {/* ROI Calculator Section */}
      <section className={styles.roiCalculator} id="roi">
        <h2 className={styles.sectionTitle}>See Your Return On Investment</h2>
        <div className={styles.calculatorContainer}>
          <p>Here's how QR Butler can impact your restaurant's bottom line:</p>
          
          <table className={styles.roiTable}>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Before QR Butler</th>
                <th>With QR Butler</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Average Order Value</td>
                <td>$20.00</td>
                <td>$24.00 <small>(+20%)</small></td>
              </tr>
              <tr>
                <td>Orders Per Day</td>
                <td>100</td>
                <td>120 <small>(+20%)</small></td>
              </tr>
              <tr>
                <td>Front-of-House Staff</td>
                <td>5 servers</td>
                <td>3 servers <small>(-40%)</small></td>
              </tr>
              <tr>
                <td>Monthly Staff Cost</td>
                <td>$12,500</td>
                <td>$7,500 <small>(-$5,000)</small></td>
              </tr>
              <tr>
                <td>QR Butler Fee (6%)</td>
                <td>$0</td>
                <td>$2,592</td>
              </tr>
              <tr className={styles.roiHighlight}>
                <td>Monthly Net Benefit</td>
                <td>-</td>
                <td>+$7,408</td>
              </tr>
            </tbody>
          </table>
          
          <p>Results vary by establishment. This example represents a typical medium-sized restaurant implementing QR Butler for all orders.</p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className={styles.finalCta}>
        <h2>Ready to transform your restaurant?</h2>
        <div className={styles.authButtons}>
          <Link to="/signup" className={styles.primaryButton}>Sign Up Free</Link>
          <Link to="/login" className={styles.secondaryButton}>Log In</Link>
        </div>
      </section>
    </div>
  );
}